import React, { useEffect, useMemo, useState } from "react";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { faChevronCircleLeft, faEdit, faEllipsisV, faPen, faPencil, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/* -------------------- GLOBAL SEARCH -------------------- */

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <input
      className="form-control"
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value)}
      placeholder="Search..."
    />
  );
};

/* -------------------- COMPONENT -------------------- */

const RecipeMasterList = ({permissions}) => {

  const token = localStorage.getItem("token");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchRecipeList = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(
        `${SERVER_BASE}/getRecipeList`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(res.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load recipes", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipeList();
  }, []);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This recipe will be permanently deleted",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiClient.delete(
            `${SERVER_BASE}/deleteRecipe/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
  
          Swal.fire({
            icon: "success",
            text: "Recipe deleted",
            timer: 1500,
            showConfirmButton: false,
          });
  
          fetchRecipeList(); // 🔄 refresh list
        } catch (error) {
          console.error(error);
          Swal.fire("Error", "Delete failed", "error");
        }
      }
    });
  };
  
  /* -------------------- VIEW ITEMS -------------------- */

  const viewItems = (items) => {
    const html = `
      <table class="table table-bordered">
        <thead>
          <tr>
          <th>Item</th>
          <th>Qty</th>
          <th>UOM</th>
          </tr>
        </thead>
        <tbody>
          ${items
            .map(
              (i) => `
              <tr>
                <td>${i.itemName}</td>
                <td>${i.qty}</td>
                <td>${i.uom}</td>
              </tr>`
            )
            .join("")}
        </tbody>
      </table>
    `;

    Swal.fire({
      title: "Recipe Items",
      html,
      width: "700px",
    });
  };

  /* -------------------- COLUMNS -------------------- */

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: (_, i) => i + 1,
        disableSortBy: true,
      },
      {
        Header: "Department",
        accessor: "department",
      },
      {
        Header: "Produced Item",
        accessor: "producingItem",
      },
      {
        Header: "Produced Qty",
        accessor: (row) =>
          `${row.producingQty} ${row.producingUom || ""}`,
      },
      {
        Header: "Persons",
        accessor: "persons",
      },
      {
        Header: "Total Items",
        accessor: (row) => row.items?.length || 0,
        disableSortBy: true,
      },
      // {
      //   Header: "Created On",
      //   accessor: (row) =>
      //     moment(row.createdAt).format("DD-MMM-YYYY"),
      // },
      {
        Header: "Preview",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
              {permissions?.read && (
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() => viewItems(row.original.items)}
            >
              View
            </button>
              )}
            {/* {permissions?.edit && (
<button
className="btn btn-sm btn-warning"
onClick={() =>
  navigate("../Recipe", {
    state: { id: row.original.id },
  })
}
>
Edit
</button>
 )} */}

      {/* {permissions?.delete && (
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(row.original.id)}
            >
              Delete
            </button>
            )} */}
          </div>
        ),
      },
      {
        Header: "#",
        accessor: "actions",
        Cell: ({ row }) => {
          const creator = row?.original?.creator;
          const updater = row?.original?.updater;
 
          const createdBy = creator
            ? [creator.firstName, creator.lastName].filter(Boolean).join(" ")
            : null;
      
          const updatedBy = updater
            ? [updater.firstName, updater.lastName].filter(Boolean).join(" ")
            : null;
      
          const createdAt = row?.original?.createdAt;
          const updatedAt = row?.original?.updatedAt;
      
          return (
            <div className="dropdown">
              <button
                className="btn btn-light"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>
      
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                 {permissions?.edit && (
                   <li>
<button
className="dropdown-item"
onClick={() =>
  navigate("../Recipe", {
    state: { id: row.original.id },
  })
}
>
<FontAwesomeIcon icon={faEdit} /> Edit
</button>
</li>
 )}

                      {permissions?.delete && (
                                  <li>
                                    <button
                                      className="dropdown-item text-danger"
                                      onClick={() => handleDelete(row.original.id)}
                                    >
                                      <FontAwesomeIcon icon={faTrash} /> Delete
                                    </button>
                                  </li>
                                )}
                {(createdBy || updatedBy) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
      
                {createdBy && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    {createdBy}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
      
                {updatedBy && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} className="me-1" />
                    {updatedBy}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      }
      
      
    ],
    [permissions]
  );


  /* -------------------- TABLE INSTANCE -------------------- */

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  /* -------------------- UI -------------------- */

  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-3">Recipe Master List</h4>

    
      <div className="mb-3 d-flex gap-2">
        {permissions?.write && (
      <button
            className="btn btn-sm btn-outline-success "
            style={{width:"15%"}}
            onClick={() => {
              navigate("../Recipe");
            }}
          >
            Add Recipe
          </button>
          )}
      {/* Search */}
      <GlobalFilter
       className="w-50"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
         <div>
        <select
          className="form-select w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[5, 10, 20, 50].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select></div>

      </div>

      {/* Table */}
      {permissions?.read && (<>
      <div className="">
        <table
          {...getTableProps()}
          className="table table-bordered table-striped"
        >
          <thead className="table-light">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} >
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(
                      column.getSortByToggleProps()
                    )}
                  >
                    {column.render("Header")}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : page.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  No records found
                </td>
              </tr>
            ) : (
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} style={{alignContent:'center'}}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
     

      

      <div className="d-flex justify-content-between">
                  <button className="btn btn-secondary" onClick={previousPage} disabled={!canPreviousPage}>
                    <FontAwesomeIcon icon={faChevronCircleLeft}></FontAwesomeIcon> Previous
                  </button>
                  <span>  Page {pageIndex + 1} of {pageOptions.length}</span>
      
                  <button className="btn btn-secondary" onClick={nextPage} disabled={!canNextPage}>
                    Next <FontAwesomeIcon icon={faChevronCircleLeft} rotation={180}></FontAwesomeIcon>
                  </button>
                </div>
                </> )}
      </div>
  );
};

export default RecipeMasterList;
